export type CategoryTreeNode = {
  id: string;
  level: number;
  parentId?: string | null;
};

export type CategoryNodeWithChildren<T extends CategoryTreeNode> = T & {
  children: Array<CategoryNodeWithChildren<T>>;
};

const buildTreeByParentId = <T extends CategoryTreeNode>(
  flatCategories: T[],
): Array<CategoryNodeWithChildren<T>> => {
  const categoryById = new Map<string, CategoryNodeWithChildren<T>>();
  const roots: Array<CategoryNodeWithChildren<T>> = [];

  for (const category of flatCategories) {
    if (!categoryById.has(category.id)) {
      categoryById.set(category.id, { ...category, children: [] });
    }
  }

  const processed = new Set<string>();
  for (const category of flatCategories) {
    if (!processed.has(category.id)) {
      processed.add(category.id);

      const node = categoryById.get(category.id);
      if (node) {
        const hasValidParent =
          category.parentId &&
          category.parentId !== category.id &&
          categoryById.has(category.parentId);

        if (hasValidParent) {
          categoryById.get(category.parentId!)!.children.push(node);
        } else {
          roots.push(node);
        }
      }
    }
  }

  return roots;
};

const buildTreeByLevel = <T extends CategoryTreeNode>(
  flatCategories: T[],
): Array<CategoryNodeWithChildren<T>> => {
  const roots: Array<CategoryNodeWithChildren<T>> = [];
  const stack: Array<{ level: number; node: CategoryNodeWithChildren<T> }> = [];

  for (const category of flatCategories) {
    if (stack.length > 0) {
      const maxLevel = stack[stack.length - 1].level;
      if (category.level > maxLevel + 1) {
        throw new Error(
          `In category ${category.id}: Invalid level jump (level ${category.level}) after level ${maxLevel}`,
        );
      }
    } else if (category.level !== 0) {
      throw new Error(
        `In category ${category.id}: First category must be level 0, got ${category.level}`,
      );
    }

    const node: CategoryNodeWithChildren<T> = { ...category, children: [] };

    while (stack.length > 0 && stack[stack.length - 1].level >= category.level) {
      stack.pop();
    }

    if (stack.length === 0) {
      roots.push(node);
    } else {
      stack[stack.length - 1].node.children.push(node);
    }

    stack.push({ level: category.level, node });
  }

  return roots;
};

export const buildCategoryTree = <T extends CategoryTreeNode>(
  flatCategories: T[],
): Array<CategoryNodeWithChildren<T>> => {
  if (flatCategories.length === 0) return [];

  const hasParentId = flatCategories.some((cat) => cat.parentId !== undefined);

  return hasParentId ? buildTreeByParentId(flatCategories) : buildTreeByLevel(flatCategories);
};
